import { useState, useCallback } from 'react';
import { useTicketCloseMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../../services/profileService/profile-api';
import { TicketCloseCommand } from '../../../../../services/ticketEasyService/commands/approval/TicketCloseCommand';

interface UseTicketCloseProps {
  ticketId?: string;
  onSuccess?: () => void;
}

export function useTicketClose({ ticketId, onSuccess }: UseTicketCloseProps) {
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeSuccess, setCloseSuccess] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  
  const { data: myProfile } = useMyProfileQuery();
  const [closeTicket] = useTicketCloseMutation();
  
  const openCloseDialog = useCallback(() => {
    setCloseDialogOpen(true);
  }, []);
  
  const closeCloseDialog = useCallback(() => {
    setCloseDialogOpen(false);
  }, []);
  
  const handleCloseSubmit = useCallback(async (comments: string) => {
    if (!ticketId) {
      setCloseError("Cannot close ticket: Missing ticket ID");
      return;
    }
    
    if (!myProfile) {
      setCloseError("Cannot close ticket: Missing user data");
      return;
    }
    
    setClosing(true);
    setCloseError(null);
    
    try {
      const closeCommand = new TicketCloseCommand({
        ticketId,
        closedBy: myProfile.identity,
        closedByEmail: myProfile.email,
        comments
      });
      
      await closeTicket(closeCommand).unwrap();
      setCloseSuccess(true);
      closeCloseDialog();
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Auto-hide success message after 6 seconds
      setTimeout(() => {
        setCloseSuccess(false);
      }, 6000);
    } catch (error) {
      console.error("Failed to close ticket:", error);
      setCloseError("Failed to close ticket. Please try again.");
    } finally {
      setClosing(false);
    }
  }, [ticketId, myProfile, closeTicket, closeCloseDialog, onSuccess]);
  
  const clearSuccessMessage = useCallback(() => {
    setCloseSuccess(false);
  }, []);
  
  const clearErrorMessage = useCallback(() => {
    setCloseError(null);
  }, []);
  
  return {
    closeDialogOpen,
    closing,
    closeSuccess,
    closeError,
    openCloseDialog,
    closeCloseDialog,
    handleCloseSubmit,
    clearSuccessMessage,
    clearErrorMessage
  };
}
